import { displayPortCode, type ItineraryStop, voyageItinerary } from '@felix/domain';
import { ActionIcon, Button, Skeleton } from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowDown,
  IconArrowUp,
  IconBulb,
  IconDatabasePlus,
  IconPlus,
  IconRoute,
  IconTrash,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { useFitColumns } from '../../fit-columns.js';
import { PortPicker, type PortPickerValue } from '../../inputs/PortPicker.js';
import { useUnsavedChanges } from '../../shell-context.js';
import table from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import shared from '../voyage/NoonReportScreen.module.css';
import { SectionTitle } from '../voyage/NoonSections.js';
import { formatPlannedTime, plannedFact } from '../voyage/noon-report.js';
import { useNumber } from '../voyage/numbers.js';
import type { VoyageSource } from '../voyage/source.js';
import type { Voyage } from '../voyage/types.js';
import { VoyageFrame } from '../voyage/VoyageFrame.js';
import { useCountryName, usePortCatalog } from './catalog-context.js';
import { type RouteOrderSuggestion, routeLegs, routeOrderSuggestion } from './legs.js';
import classes from './Route.module.css';
import { SourceBadge } from './SourceBadge.js';
import type { PortsSource } from './source.js';
import type { PortDistance, VoyagePortEntry, VoyageRoute } from './types.js';

export interface RouteScreenProps {
  source: VoyageSource;
  ports: PortsSource;
}

type Notice = { tone: 'success' | 'danger' | 'info'; text: string };

/**
 * Voyage ▸ Route & Distances (ADR 0048 §5–§6): seferin limanları (from + ara limanlar + to),
 * bacak bacak mesafe, kaynak rozeti, toplam ve mesafeye göre sıra önerisi. Mesafe şirket
 * tablosundan, yoksa paketli açık veriyle tahmin edilir (yaklaşık; seyir için değil). Ara
 * limanları gemi yazar; ofis görür ve tahmini tabloya kaydedebilir. Harita yok (çevrimdışı).
 */
export function RouteScreen({ source, ports }: RouteScreenProps) {
  const { t } = useTranslation();
  const n = useNumber();
  const k = (key: string) => t(`ports.route.${key}`);
  return (
    <VoyageFrame
      source={source}
      title={k('title')}
      description={k('description')}
      facts={(voyage) => [
        { label: t('voyage.noon.header.voyageNo'), value: voyage.voyageNo },
        { label: k('planned'), value: n.unit(voyage.plannedDistanceNm, 'NM', 0) },
        // ETD güzergâhın kalkış satırında (başlık kartı dar; ADR 0054 §2)
        plannedFact(
          t('voyage.planner.plannedEta'),
          voyage.plannedEta,
          voyage.plannedEtaTzOffsetMin,
        ),
      ]}
      skeleton={
        <div className={classes.body}>
          <Skeleton height={360} radius="md" />
          <Skeleton height={260} radius="md" />
        </div>
      }
    >
      {(data) => (
        <RouteBody key={data.voyage.id} voyage={data.voyage} source={source} ports={ports} />
      )}
    </VoyageFrame>
  );
}

const viasOf = (route: VoyageRoute): VoyagePortEntry[] =>
  route.vias.map((via) => ({ portCode: via.portCode, portName: via.portName }));

function RouteBody({
  voyage,
  source,
  ports,
}: {
  voyage: Voyage;
  source: VoyageSource;
  ports: PortsSource;
}) {
  const { t } = useTranslation();
  const n = useNumber();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`ports.route.${key}`, options);
  const error = (code: string) =>
    t(`ports.errors.${code}`, { defaultValue: t('ports.errors.GENERIC') });
  const { catalog, sea, failed } = usePortCatalog();
  const countryName = useCountryName();
  const [route, setRoute] = useState<VoyageRoute | null>(null);
  const [distances, setDistances] = useState<PortDistance[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<VoyagePortEntry[] | null>(null);
  const [adding, setAdding] = useState<PortPickerValue>({ name: '', code: null });
  const [suggestion, setSuggestion] = useState<RouteOrderSuggestion | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [busy, setBusy] = useState(false);
  const tableBox = useFitColumns<HTMLElement>();

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [nextRoute, nextDistances] = await Promise.all([
        ports.route(voyage.id),
        ports.distances(),
      ]);
      setRoute(nextRoute);
      setDistances(nextDistances);
    } catch (failure) {
      setLoadError(errorCode(failure));
    }
  }, [ports, voyage.id]);
  useEffect(() => {
    void load();
  }, [load]);

  const editable =
    !!ports.saveRoute && voyage.status !== 'cancelled' && voyage.status !== 'completed';
  const vias = draft ?? (route ? viasOf(route) : []);
  const dirty = draft !== null;
  useUnsavedChanges(dirty);

  const stops: ItineraryStop[] = useMemo(
    () =>
      voyageItinerary(
        {
          fromPort: voyage.fromPort,
          fromPortCode: voyage.fromPortCode ?? null,
          toPort: voyage.toPort,
          toPortCode: voyage.toPortCode ?? null,
        },
        vias.map((via, index) => ({ id: `via-${index}`, seq: index + 1, ...via })),
      ),
    [voyage, vias],
  );
  const result = useMemo(
    () => (catalog ? routeLegs(stops, catalog, distances) : null),
    [catalog, stops, distances],
  );

  if (loadError) {
    return (
      <section className={shared.card}>
        <EmptyState
          tone="danger"
          title={k('dataError')}
          description={error(loadError)}
          action={
            <Button variant="light" onClick={() => void load()}>
              {t('common.retry')}
            </Button>
          }
        />
      </section>
    );
  }
  if (!route || (!catalog && !failed)) {
    return (
      <div className={classes.body} aria-busy="true">
        <Skeleton height={360} radius="md" />
        <Skeleton height={260} radius="md" />
      </div>
    );
  }

  const setVias = (next: VoyagePortEntry[]) => {
    setDraft(next);
    setSuggestion(null);
  };
  const move = (index: number, by: number) => {
    const next = [...vias];
    const [item] = next.splice(index, 1);
    if (item) next.splice(index + by, 0, item);
    setVias(next);
  };
  const save = async () => {
    if (!ports.saveRoute || !draft) return;
    setBusy(true);
    try {
      setRoute(await ports.saveRoute(voyage.id, draft));
      setDraft(null);
      setNotice({ tone: 'success', text: k('saved') });
    } catch (failure) {
      setNotice({ tone: 'danger', text: error(errorCode(failure)) });
    } finally {
      setBusy(false);
    }
  };
  const total = result?.totalNm ?? null;
  const applyPlanned = async () => {
    if (!source.planner || total === null) return;
    setBusy(true);
    try {
      await source.planner.update(voyage.id, {
        rowVersion: voyage.rowVersion,
        plannedDistanceNm: Math.round(total),
      });
      setNotice({ tone: 'success', text: k('plannedSaved') });
    } catch (failure) {
      setNotice({ tone: 'danger', text: error(errorCode(failure)) });
    } finally {
      setBusy(false);
    }
  };
  const saveToTable = async (from: ItineraryStop, to: ItineraryStop) => {
    if (!ports.editor || !from.portCode || !to.portCode) return;
    try {
      await ports.editor.createDistance({
        source: 'computed',
        portA: from.portCode,
        portB: to.portCode,
        note: null,
      });
      setDistances(await ports.distances());
      setNotice({ tone: 'success', text: k('addedToTable') });
    } catch (failure) {
      setNotice({ tone: 'danger', text: error(errorCode(failure)) });
    }
  };
  const applySuggestion = () => {
    if (suggestion?.kind !== 'ok') return;
    const byKey = new Map(stops.map((stop) => [stop.key, stop]));
    setDraft(
      suggestion.order.map((key) => {
        const stop = byKey.get(key) as ItineraryStop;
        return { portCode: stop.portCode, portName: stop.portName };
      }),
    );
    setSuggestion(null);
    setNotice({ tone: 'info', text: k('applied') });
  };

  const legs = result?.legs ?? [];
  const storedCount = legs.filter((leg) => leg.stored).length;
  const estimatedCount = legs.filter((leg) => !leg.stored && leg.distanceNm !== null).length;
  // Kümülatif: ilk bilinmeyen bacaktan sonra boş
  const cumulatives: (number | null)[] = [0];
  for (const leg of legs) {
    const last = cumulatives.at(-1) ?? null;
    cumulatives.push(last === null || leg.distanceNm === null ? null : last + leg.distanceNm);
  }
  const difference = total === null ? null : total - voyage.plannedDistanceNm;

  return (
    <div className={classes.body}>
      <div className={classes.main}>
        <section className={shared.card} aria-label={k('card')}>
          <SectionTitle icon={<IconRoute size={18} />} title={k('card')} />
          {/* Şematik şerit: haritanın yerine; çevrimdışı çalışır */}
          <ol className={classes.strip} aria-hidden>
            {stops.map((stop, index) => {
              const leg = index > 0 ? legs[index - 1] : undefined;
              return [
                leg ? (
                  <li
                    key={`leg-${stop.key}`}
                    className={classes.leg}
                    data-known={leg.distanceNm !== null || undefined}
                  >
                    <span className={classes.legLabel}>
                      {leg.distanceNm === null ? '—' : `${n(leg.distanceNm, 0)} NM`}
                    </span>
                  </li>
                ) : null,
                <li key={stop.key} className={classes.stop} data-role={stop.role}>
                  <span className={classes.dot} />
                  <span className={classes.stopName} title={stop.portName}>
                    {stop.portName}
                  </span>
                  <span className={classes.stopMeta}>{k(`role.${stop.role}`)}</span>
                </li>,
              ];
            })}
          </ol>

          <section
            ref={tableBox}
            className={table.tableWrap}
            aria-label={k('card')}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            {/* Otomatik düzen: sütunlar içeriğe göre; dar kutuda önce kümülatif, sonra ülke gizlenir */}
            <table className={`${table.table} ${classes.table}`} style={{ tableLayout: 'auto' }}>
              <thead>
                <tr>
                  <th scope="col" className={classes.colNo}>
                    {k('columns.no')}
                  </th>
                  <th scope="col">{k('columns.port')}</th>
                  <th scope="col" data-drop={2}>
                    {k('columns.country')}
                  </th>
                  <th scope="col" data-numeric>
                    {k('columns.leg')}
                  </th>
                  <th scope="col">{k('columns.source')}</th>
                  <th scope="col" data-numeric data-drop={1}>
                    {k('columns.cumulative')}
                  </th>
                  {editable || ports.editor ? (
                    <th scope="col" data-numeric className={classes.colActions}>
                      {k('columns.actions')}
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {stops.map((stop, index) => {
                  const leg = index > 0 ? legs[index - 1] : undefined;
                  const port = stop.portCode ? catalog?.byCode.get(stop.portCode) : undefined;
                  const viaIndex = index - 1;
                  const previous = stops[index - 1];
                  return (
                    <tr key={stop.key}>
                      <td>{index + 1}</td>
                      <td>
                        <div className={classes.portCell}>
                          <span className={classes.portName}>{stop.portName}</span>
                          <span className={classes.portRole}>
                            {k(`role.${stop.role}`)}
                            {displayPortCode(stop.portCode) ? (
                              <span className={classes.code}>{stop.portCode}</span>
                            ) : null}
                            {!stop.portCode ? (
                              <span className={classes.missing}> · {k('noCode')}</span>
                            ) : null}
                          </span>
                          {/* Planlanan kalkış ve varış limanın yerel saatiyle (ADR 0054 §2) */}
                          {stop.role === 'from' && voyage.plannedEtd ? (
                            <span className={classes.planned}>
                              {`${t('voyage.planner.plannedEtd')}: ${formatPlannedTime(voyage.plannedEtd, voyage.plannedEtdTzOffsetMin)}`}
                            </span>
                          ) : null}
                          {stop.role === 'to' ? (
                            <span className={classes.planned}>
                              {`${t('voyage.planner.plannedEta')}: ${formatPlannedTime(voyage.plannedEta, voyage.plannedEtaTzOffsetMin)}`}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td data-drop={2}>{port ? countryName(port.country) : '—'}</td>
                      <td data-numeric>
                        {leg
                          ? leg.distanceNm === null
                            ? leg.reason === 'NO_ROUTE'
                              ? k('noRoute')
                              : '—'
                            : n(leg.distanceNm, 1)
                          : '—'}
                      </td>
                      <td>{leg ? <SourceBadge source={leg.source} stored={leg.stored} /> : '—'}</td>
                      <td data-numeric data-drop={1}>
                        {cumulatives[index] == null ? '—' : n(cumulatives[index], 1)}
                      </td>
                      {editable || ports.editor ? (
                        <td data-numeric>
                          <span className={classes.rowActions}>
                            {editable && stop.role === 'via' ? (
                              <>
                                <ActionIcon
                                  variant="subtle"
                                  color="gray"
                                  disabled={viaIndex === 0}
                                  aria-label={k('moveUp', { port: stop.portName })}
                                  onClick={() => move(viaIndex, -1)}
                                >
                                  <IconArrowUp size={15} />
                                </ActionIcon>
                                <ActionIcon
                                  variant="subtle"
                                  color="gray"
                                  disabled={viaIndex === vias.length - 1}
                                  aria-label={k('moveDown', { port: stop.portName })}
                                  onClick={() => move(viaIndex, 1)}
                                >
                                  <IconArrowDown size={15} />
                                </ActionIcon>
                                <ActionIcon
                                  variant="subtle"
                                  color="red"
                                  aria-label={k('remove', { port: stop.portName })}
                                  onClick={() => setVias(vias.filter((_, i) => i !== viaIndex))}
                                >
                                  <IconTrash size={15} />
                                </ActionIcon>
                              </>
                            ) : null}
                            {ports.editor &&
                            leg &&
                            !leg.stored &&
                            leg.distanceNm !== null &&
                            previous ? (
                              <ActionIcon
                                variant="subtle"
                                aria-label={`${k('addToTable')}: ${k('legOf', {
                                  from: previous.portName,
                                  to: stop.portName,
                                })}`}
                                onClick={() => void saveToTable(previous, stop)}
                              >
                                <IconDatabasePlus size={15} />
                              </ActionIcon>
                            ) : null}
                          </span>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
                <tr className={classes.totalRow}>
                  <td />
                  <td>{k('total')}</td>
                  <td data-drop={2} />
                  <td data-numeric>{total === null ? '—' : n(total, 1)}</td>
                  <td />
                  <td data-drop={1} />
                  {editable || ports.editor ? <td /> : null}
                </tr>
              </tbody>
            </table>
          </section>

          {editable ? (
            <div className={classes.addRow}>
              <PortPicker
                label={k('addPort')}
                value={adding}
                onChange={setAdding}
                classNames={{ label: shared.label ?? '' }}
              />
              <Button
                variant="light"
                leftSection={<IconPlus size={16} />}
                disabled={adding.name.trim() === ''}
                onClick={() => {
                  setVias([...vias, { portCode: adding.code, portName: adding.name.trim() }]);
                  setAdding({ name: '', code: null });
                }}
              >
                {k('add')}
              </Button>
            </div>
          ) : null}
          {dirty ? (
            <div className={classes.actions}>
              <span className={classes.footnote}>{k('unsaved')}</span>
              <Button variant="default" onClick={() => setDraft(null)} disabled={busy}>
                {k('discard')}
              </Button>
              <Button onClick={() => void save()} loading={busy}>
                {k('save')}
              </Button>
            </div>
          ) : null}
          {!ports.saveRoute ? <p className={classes.footnote}>{k('officeNote')}</p> : null}
        </section>
        {notice ? (
          <p
            className={classes.notice}
            data-tone={notice.tone}
            role={notice.tone === 'danger' ? 'alert' : 'status'}
          >
            {notice.text}
          </p>
        ) : null}
      </div>

      <div className={classes.side}>
        <section className={shared.card} aria-label={k('summary')}>
          <SectionTitle icon={<IconRoute size={18} />} title={k('summary')} />
          <dl className={classes.figures}>
            <dt>{k('totalDistance')}</dt>
            <dd>{total === null ? '—' : n.unit(total, 'NM', 0)}</dd>
            <dt>{k('planned')}</dt>
            <dd>{n.unit(voyage.plannedDistanceNm, 'NM', 0)}</dd>
            <dt>{k('difference')}</dt>
            <dd
              data-tone={
                difference !== null && Math.abs(difference) > voyage.plannedDistanceNm * 0.05
                  ? 'warning'
                  : undefined
              }
            >
              {difference !== null && Math.abs(difference) > voyage.plannedDistanceNm * 0.05 ? (
                <IconAlertTriangle size={14} aria-hidden />
              ) : null}
              {difference === null
                ? '—'
                : `${difference > 0 ? '+' : ''}${n.unit(difference, 'NM', 0)}`}
            </dd>
            <dt>{k('legs')}</dt>
            <dd>{legs.length}</dd>
            <dt>{k('storedLegs')}</dt>
            <dd>{storedCount}</dd>
            <dt>{k('computedLegs')}</dt>
            <dd>{estimatedCount}</dd>
          </dl>
          {source.planner &&
          total !== null &&
          !dirty &&
          Math.round(total) !== Math.round(voyage.plannedDistanceNm) ? (
            <Button variant="light" onClick={() => void applyPlanned()} loading={busy}>
              {k('usePlanned')}
            </Button>
          ) : null}
        </section>

        <section className={shared.card} aria-label={k('suggestTitle')}>
          <SectionTitle icon={<IconBulb size={18} />} title={k('suggestTitle')} />
          <Button
            variant="default"
            disabled={!catalog}
            onClick={() =>
              catalog && setSuggestion(routeOrderSuggestion(stops, catalog, distances))
            }
          >
            {k('suggest')}
          </Button>
          {suggestion ? (
            <div className={classes.suggestion} role="status">
              {suggestion.kind === 'nothing' ? <span>{k('nothing')}</span> : null}
              {suggestion.kind === 'missing-code' ? <span>{k('missingCode')}</span> : null}
              {suggestion.kind === 'no-route' ? <span>{k('noRouteSuggest')}</span> : null}
              {suggestion.kind === 'ok' ? (
                suggestion.suggestedNm >= suggestion.currentNm - 0.5 ? (
                  <span>{k('alreadyBest')}</span>
                ) : (
                  <>
                    <span>
                      {k('suggestCurrent')}: {n.unit(suggestion.currentNm, 'NM', 0)}
                      <br />
                      {k('suggestNew')}: {n.unit(suggestion.suggestedNm, 'NM', 0)}{' '}
                      <span className={classes.saving}>
                        ({k('saving', { nm: n(suggestion.currentNm - suggestion.suggestedNm, 0) })})
                      </span>
                    </span>
                    <ol className={classes.order}>
                      {suggestion.order.map((key) => (
                        <li key={key}>{stops.find((stop) => stop.key === key)?.portName}</li>
                      ))}
                    </ol>
                    {!suggestion.exact ? (
                      <span className={classes.footnote}>{k('heuristic')}</span>
                    ) : null}
                    {editable ? <Button onClick={applySuggestion}>{k('apply')}</Button> : null}
                  </>
                )
              ) : null}
            </div>
          ) : null}
        </section>

        <p className={classes.footnote}>
          {t('ports.approx')}
          <br />
          {sea
            ? t('ports.attribution', {
                network: sea.attribution.network,
                ports: sea.attribution.ports,
              })
            : null}
        </p>
      </div>
    </div>
  );
}
